import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Home as HomeIcon,
  UserPlus,
  ClipboardList,
  Sparkles,
  ListChecks,
  Clock,
  Download,
  CheckCircle2,
  HelpCircle,
  Megaphone,
  ShieldCheck,
  RefreshCw,
  FileText,
  type LucideIcon,
} from 'lucide-react';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useCounts, useEtlStatus, useMe } from '@/api/hooks';
import { useToast } from './Toast';
import { BrcLogo } from './BrcLogo';

// Stamped at build time by vite.config.ts (release number from src/web/VERSION
// + git short hash + build date).
const APP_VERSION = __APP_VERSION__;

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  external?: string;
}

const PRIMARY: NavItem[] = [
  { to: '/', label: 'HOME', icon: HomeIcon, end: true },
  { to: '/clients/new', label: 'NEW CLIENT', icon: UserPlus },
  { to: '/clients', label: 'VIEW / CHANGE CLIENTS', icon: ClipboardList, end: true },
  { to: '/engagements/new', label: 'NEW ENGAGEMENT', icon: Sparkles },
  { to: '/engagements', label: 'VIEW / CHANGE ENGAGEMENTS', icon: ListChecks, end: true },
];
const SECONDARY: NavItem[] = [
  { to: '/pending', label: 'PENDING REQUESTS', icon: Clock, end: true },
  { to: '/pending/download', label: 'VIEW DOWNLOADED PENDING REQUESTS', icon: Download },
  { to: '/completed', label: 'COMPLETED REQUESTS', icon: CheckCircle2 },
  { to: '/forms', label: 'REQUEST FORMS', icon: FileText },
];

/**
 * Maconomy data-load lock — mirrors Power Apps. While one of these reference
 * tables is loading, the app puts a translucent overlay over the data-entry and
 * approval screens so nothing can be created/edited/approved against data that
 * is mid-refresh. Read-only screens (lists, Completed, Download) stay usable.
 */
const ETL_MESSAGES: Record<string, string> = {
  tb_Client_Information: 'Maconomy client data load in progress. Kindly wait for it to complete',
  tb_Employee_Information: 'Maconomy employee data load in progress. Kindly wait for it to complete',
  tb_Engagement_Service_Type:
    'Maconomy engagement service type data load in progress. Kindly wait for it to complete',
  tb_Engagement_Location:
    'Maconomy engagement location data load in progress. Kindly wait for it to complete',
  tb_Client_Industry: 'Maconomy client industry data load in progress. Kindly wait for it to complete',
};

// Routes locked during a load (the screens Power Apps covers with its overlay):
// New/Edit Client, New/Edit Engagement, Pending Requests. The ".../additional"
// and "/pending/download" routes are read-only, so they are intentionally not locked.
const LOCKED_ROUTES: RegExp[] = [
  /^\/clients\/new$/,
  /^\/clients\/[^/]+\/[^/]+$/,
  /^\/engagements\/new$/,
  /^\/engagements\/[^/]+$/,
  /^\/pending$/,
];

const TITLES: Record<string, string> = {
  '/': 'HOME',
  '/clients': 'VIEW / CHANGE CLIENTS',
  '/clients/new': 'NEW CLIENT',
  '/engagements': 'VIEW / CHANGE ENGAGEMENTS',
  '/engagements/new': 'NEW ENGAGEMENT',
  '/pending': 'PENDING REQUESTS',
  '/pending/download': 'DOWNLOAD PENDING REQUESTS',
  '/completed': 'COMPLETED REQUESTS',
  '/forms': 'REQUEST FORMS',
  '/users': 'MANAGE USERS',
};

function MenuLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold tracking-wide transition-colors ${
          isActive ? 'bg-secondary text-primary' : 'text-foreground hover:bg-secondary/60'
        }`
      }
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Icon className="h-4 w-4" />
      </span>
      {item.label}
    </NavLink>
  );
}

export function AppShell() {
  const { data: me } = useMe();
  const { data: counts } = useCounts();
  const { data: etl } = useEtlStatus();
  const location = useLocation();
  const title = TITLES[location.pathname] ?? '';

  // Manual "refresh all data" — mirrors the Power App TitleBar globe-refresh icon,
  // which re-pulls every reference table + client/engagement view so an open session
  // isn't stuck on stale data (e.g. after a Maconomy load or another user's change).
  // One invalidateQueries() marks the whole cache stale; active screens refetch now.
  const qc = useQueryClient();
  const isFetching = useIsFetching();
  const notify = useToast();
  async function refreshAll() {
    await qc.invalidateQueries();
    notify('success', 'Data refreshed.');
  }

  // Which load is running (drives the exact message), and is this screen locked?
  const runningTable = (etl?.tables ?? []).find(
    (t) => ETL_MESSAGES[t.TableName] && t.LatestRunStatus === 'In Progress',
  );
  const etlMessage = runningTable ? ETL_MESSAGES[runningTable.TableName] : '';
  const screenLocked = !!etl?.inProgress && LOCKED_ROUTES.some((re) => re.test(location.pathname));

  return (
    // h-screen + overflow-hidden = app is exactly the viewport; the header/sidebar
    // stay fixed and each page scrolls inside <main>, so a wide table's horizontal
    // scrollbar stays visible instead of sitting at the bottom of a 500-row page.
    <div className="flex h-screen flex-col overflow-hidden bg-card">
      {/* Crimson top bar */}
      <div className="brc-gradient flex items-center gap-4 px-4 py-2 text-white">
        <span className="text-sm font-semibold">BRC Client Administration V2</span>
      </div>

      {/* Logo / user / title / version row */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border px-4 py-3">
        <div className="flex items-center gap-4">
          <Link to="/" title="Home" className="transition hover:opacity-80">
            <BrcLogo />
          </Link>
          <span className="text-sm font-semibold text-primary">
            Logged in user: {me?.name ?? '—'}
          </span>
        </div>
        <span className="text-lg font-bold tracking-wide text-primary">{title}</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={refreshAll}
            title="Refresh all data"
            aria-label="Refresh all data"
            className="flex h-9 w-9 items-center justify-center rounded-full text-primary transition-colors hover:bg-secondary disabled:opacity-60"
            disabled={!!isFetching}
          >
            <RefreshCw className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <span className="text-sm font-semibold text-primary">Version: {APP_VERSION}</span>
        </div>
      </div>

      {etl?.inProgress ? (
        <div className="bg-warning px-6 py-2 text-sm font-medium text-warning-foreground">
          {etlMessage || 'Maconomy data load in progress. Kindly wait for it to complete'} — creating,
          editing and approving are disabled until it finishes.
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        {/* Left nav */}
        <aside className="flex w-72 shrink-0 flex-col justify-between overflow-y-auto border-r border-border bg-secondary/30 p-3">
          <nav className="space-y-1">
            {PRIMARY.map((i) => (
              <MenuLink key={i.to} item={i} />
            ))}
            <div className="my-2 border-t border-accent/40" />
            {SECONDARY.map((i) => (
              <MenuLink key={i.to} item={i} />
            ))}
            <a
              href="https://brccpa.sharepoint.com/sites/PracticeManagement"
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold tracking-wide text-foreground hover:bg-secondary/60"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <HelpCircle className="h-4 w-4" />
              </span>
              HELP
            </a>
            {me?.isSuperAdmin && (
              <>
                <div className="my-2 border-t border-accent/40" />
                <MenuLink item={{ to: '/users', label: 'MANAGE USERS', icon: ShieldCheck, end: true }} />
              </>
            )}
          </nav>

          <div className="flex items-start gap-2 px-2 py-3 text-xs text-primary">
            <Megaphone className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              You have {counts?.pendingClients ?? 0} pending client requests &{' '}
              {counts?.pendingEngagements ?? 0} pending engagement requests
            </span>
          </div>
        </aside>

        {/* min-w-0 lets this flex child shrink below its content's intrinsic width,
            so wide content scrolls inside its own container instead of pushing the
            whole page wider than the viewport (horizontal scrollbar on small displays).
            While locked we stop scrolling so the absolute overlay covers the whole area. */}
        <main
          className={`relative min-w-0 flex-1 bg-muted/40 ${screenLocked ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          <Outlet />
          {screenLocked && (
            <div
              className="absolute inset-0 z-40 flex items-start justify-center bg-muted/80 p-8 backdrop-blur-[1px]"
              role="alert"
              aria-live="polite"
            >
              <div className="card mt-8 max-w-xl text-center">
                <p className="font-semibold text-primary">{etlMessage}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  This screen is temporarily locked while Maconomy reference data is refreshed. It
                  unlocks automatically when the load finishes.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      <footer className="border-t border-border px-4 py-1.5 text-center text-xs text-muted-foreground">
        © 2026 BRC. All rights reserved.
      </footer>
    </div>
  );
}
